/**
 * Analyzer: Entry Discipline Calibration (Wave B.2, v7.4.4)
 *
 * Cross-day pattern: compares the trader's win rate and avg-R for trades
 * tagged as Stalker (waited for pullback / entered far from extreme) vs
 * Chaser (entered at fresh extreme without retracement). The educator
 * research universally claims stalker entries outperform chasers; this
 * analyzer surfaces THE TRADER'S OWN data on whether that's true for them.
 *
 * Reads:
 *   trade.barAnalysis.entryDiscipline.discipline  — 'stalker' | 'chaser' | 'neutral'
 *   trade.pnl                                      — for win/loss
 *   trade.rMultiple                                — for avg-R
 *
 * Fires when:
 *   - At least 5 trades in EACH of stalker + chaser cohorts (windowed)
 *   - WR gap between cohorts ≥ 15% in either direction
 *
 * Severity:
 *   - SUCCESS when stalker > chaser by ≥ 15% (textbook outcome — reinforce)
 *   - WARNING when chaser ≥ stalker (unusual — surfaces a calibration gap
 *     OR a setup type where chasing actually works for this trader; either
 *     way they should know)
 *
 * Window: rolling 30 trading days.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';

export const key = 'entryDisciplineCalibration';
export const tier = 'free';

const MIN_PER_COHORT = 5;
const MIN_WR_GAP     = 0.15;

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

const disciplineOf = (trade) => trade?.barAnalysis?.entryDiscipline?.discipline || null;

const isWinner = (trade) => Number.isFinite(parseFloat(trade?.pnl)) && parseFloat(trade.pnl) > 0;

const summarize = (trades) => {
  if (trades.length === 0) return { count: 0, wr: 0, avgR: 0 };
  const wins = trades.filter(isWinner).length;
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : 0;
  return { count: trades.length, wr: wins / trades.length, avgR };
};

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 30 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);

  const stalkers = [];
  const chasers = [];
  for (const t of allTrades) {
    if (!inWindow(t, fromDateStr, anchor)) continue;
    const d = disciplineOf(t);
    if (d === 'stalker') stalkers.push(t);
    else if (d === 'chaser') chasers.push(t);
    // 'neutral' / null excluded from comparison
  }

  if (stalkers.length < MIN_PER_COHORT || chasers.length < MIN_PER_COHORT) return null;

  const sStats = summarize(stalkers);
  const cStats = summarize(chasers);
  const gap = sStats.wr - cStats.wr;   // positive = stalker wins more often

  if (Math.abs(gap) < MIN_WR_GAP) return null;

  // Two narrative directions:
  if (gap > 0) {
    // Textbook outcome: stalker > chaser. Reinforce.
    return makeObservation({
      id: `${key}:${anchor}:stalker-wins`,
      analyzer: key,
      scope: 'progress',
      scopeId: anchor,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.PROCESS,
      title: `Stalker entries win ${fmtPct(Math.abs(gap))} more than chaser entries`,
      detail: `Over ${windowDays}D: ${sStats.count} stalker entries → ${fmtPct(sStats.wr)} WR (avg ${fmtR(sStats.avgR)}). ${cStats.count} chaser entries → ${fmtPct(cStats.wr)} WR (avg ${fmtR(cStats.avgR)}). Your data confirms what the educator literature claims — patience pays.`,
      action: 'When you catch yourself reaching for an entry, ask: did price come to me, or am I going to it? The number you just saw is the cost of the wrong answer.',
      evidence: { stalker: sStats, chaser: cStats, gap, windowDays },
      confidence: 0.85,
      priority: 70,
    });
  }

  // Chaser ≥ stalker — unusual signal worth a closer look.
  return makeObservation({
    id: `${key}:${anchor}:chaser-wins`,
    analyzer: key,
    scope: 'progress',
    scopeId: anchor,
    severity: SEVERITY.WARNING,
    category: CATEGORY.PROCESS,
    title: `Your chaser entries are winning ${fmtPct(Math.abs(gap))} more than stalker entries`,
    detail: `Over ${windowDays}D: ${cStats.count} chaser entries → ${fmtPct(cStats.wr)} WR (avg ${fmtR(cStats.avgR)}). ${sStats.count} stalker entries → ${fmtPct(sStats.wr)} WR (avg ${fmtR(sStats.avgR)}). Inverted from the educator playbook.`,
    action: 'Two possibilities to investigate: (a) you trade momentum setups where chasing IS the edge (3-bar plays, ORB breakouts), or (b) your "stalker" entries are second-guessed entries that miss the real move. Sample more or tag setup type to disambiguate.',
    evidence: { stalker: sStats, chaser: cStats, gap, windowDays },
    confidence: 0.75,
    priority: 76,
  });
};
